import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { Observable } from 'rxjs';
import { ButtonModule } from '@ed---interne/ng-uui-components';
import { SharedModule } from '../../../shared/shared.module';
import { Select } from '@ngxs/store';
import { AuthState } from 'src/app/core/states/auth.state';
import { User } from 'src/app/core/models/user.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TimesheetModel } from '../timesheet/timesheet.models';
import { mondayOf, TimesheetsState } from '../timesheet/timesheet.state';
import { Router, RouterModule } from '@angular/router';
import { DashboardState } from '../dashboard/dashboard.state';
import { BillingMilestoneModel } from '../project/project.models';
import {
    DisplayedColumns,
    DisplayType,
} from 'src/app/shared/components/ed-table/ed-table.component';
import { MatTableDataSource } from '@angular/material/table';

@UntilDestroy()
@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: true,
    imports: [CommonModule, RouterModule, SharedModule, ButtonModule],
})
export class HomeComponent {
    @Select(AuthState.loggedUser)
    public loggedUser$!: Observable<User | null>;
    public loggedUser!: User;

    @Select(TimesheetsState.uncompletedWeeks)
    public uncompletedWeeks$!: Observable<Date[]>;
    public uncompletedWeeks: Date[] = [];
    public valueTimesheet: string = '';
    public footerTimesheet: string = '';
    public badgeValueTimesheet: string = '';
    public badgeStyleTimesheet: 'success' | 'danger' | 'info' = 'info';

    @Select(TimesheetsState.isLoading)
    public isLoadingTimesheets$!: Observable<boolean>;
    public isLoadingTimesheets: boolean = false;

    @Select(DashboardState.billingMilestones)
    public billingMilestones$!: Observable<BillingMilestoneModel[]>;
    public billingData = new MatTableDataSource<{}>([]);
    @Select(DashboardState.isLoading)
    public isLoadingBilling$!: Observable<boolean>;
    public isLoadingBilling: boolean = false;
    public billingColumns: DisplayedColumns[] = [
        {
            displayType: DisplayType.DATE,
            objectDisplayName: 'Date prévisionnelle',
            objectKey: 'plannedDate',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code Projet',
            objectKey: 'code',
            bold: false,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Label',
            objectKey: 'label',
            bold: false,
        },
        {
            displayType: DisplayType.NUMBER,
            objectDisplayName: 'Montant',
            objectKey: 'amountExclTax',
            bold: true,
        },
        {
            displayType: DisplayType.TEXT,
            objectDisplayName: 'Code facture',
            objectKey: 'invoiceNumber',
            bold: true,
        },
    ];

    constructor(private readonly router: Router) {
        this.isLoadingTimesheets$
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => (this.isLoadingTimesheets = isLoading));

        this.loggedUser$
            .pipe(untilDestroyed(this))
            .subscribe((data: any) => (this.loggedUser = data));
        this.uncompletedWeeks$.pipe(untilDestroyed(this)).subscribe((uncompletedWeeks: Date[]) => {
            if (!uncompletedWeeks || uncompletedWeeks.length === 0) return;
            this.uncompletedWeeks = uncompletedWeeks;

            this.valueTimesheet = uncompletedWeeks
                .sort((a, b) => (a > b ? 1 : -1))[0]
                .toLocaleDateString();

            const numberOfMissingWeeks = this.uncompletedWeeks.length - 1;
            if (numberOfMissingWeeks > 1) {
                this.badgeStyleTimesheet = 'danger';
                this.badgeValueTimesheet = '!!';
                this.footerTimesheet = `Et ${numberOfMissingWeeks} autres feuilles !`;
            } else if (numberOfMissingWeeks === 1) {
                this.badgeStyleTimesheet = 'info';
                this.badgeValueTimesheet = '!';
                this.footerTimesheet = `Merci de la remplir au plus vite !`;
            } else {
                this.badgeStyleTimesheet = 'success';
                this.badgeValueTimesheet = '✔';
                this.footerTimesheet = 'Félicitation, vous êtes à jour !';
            }
        });

        this.isLoadingBilling$
            .pipe(untilDestroyed(this))
            .subscribe((isLoading) => (this.isLoadingBilling = isLoading));
        this.billingMilestones$
            .pipe(untilDestroyed(this))
            .subscribe((billingMilestones: BillingMilestoneModel[]) => {
                if (!billingMilestones) return;
                const nextMonth = new Date();
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                const data = billingMilestones
                    ?.map((billing: BillingMilestoneModel) => ({
                        ...billing,
                        plannedDate: new Date(billing.plannedDate),
                        code: billing.project?.code,
                        label: billing.project?.label,
                    }))
                    .filter(
                        (d) =>
                            d.plannedDate < nextMonth && (d.plannedDate > new Date() || !d.isSent),
                    );
                this.billingData = new MatTableDataSource<{}>(data);
            });
    }

    goToProject(projectId: any) {
        if (!projectId) return;
        this.router.navigateByUrl(`/main/projects/${projectId}/general`);
    }
}
